import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Lock, Unlock, Users, AlertTriangle } from 'lucide-react';

const API_BASE_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Zone Control Bar — shows zones for the current event session, who currently controls each,
 * and lets the logged-in user "Take Control" or "Release Control" of a zone.
 *
 * If the user is in `assigned_user_ids` of a zone, that zone is highlighted as their preferred zone.
 * Conflict resolution: another user already in control → prompt with force=true option.
 */
const ZoneControlBar = ({ sessionId, currentUser, competition, onSelectZone, selectedZone, t }) => {
  const [zoneStatus, setZoneStatus] = useState(null);
  const [busyZone, setBusyZone] = useState(null);
  const zoneConfig = competition?.zone_config || { zone_count: 1, zones: [] };

  const fetchStatus = async () => {
    if (!sessionId) return;
    try {
      const res = await axios.get(`${API_BASE_URL}/api/event-sessions/${sessionId}/zone-status`);
      setZoneStatus(res.data);
    } catch (e) { /* silent */ }
  };

  useEffect(() => {
    fetchStatus();
    const id = setInterval(fetchStatus, 5000);
    return () => clearInterval(id);
  }, [sessionId]);

  if (!sessionId || !zoneStatus || zoneStatus.zone_count < 2) return null;

  const takeControl = async (zoneNumber, force = false) => {
    setBusyZone(zoneNumber);
    try {
      const res = await axios.post(`${API_BASE_URL}/api/event-sessions/${sessionId}/zones/${zoneNumber}/take-control`, {
        user_id: currentUser?.id || 'anonymous',
        user_name: currentUser?.display_name || currentUser?.username || 'User',
        force,
      });
      if (res.data.conflict) {
        const ok = window.confirm(`${res.data.message} \n\n${(t && t('forceTakeOverPrompt')) || 'Force take over?'}`);
        if (ok) await takeControl(zoneNumber, true);
        else return;
      } else {
        if (onSelectZone) onSelectZone(zoneNumber);
      }
      fetchStatus();
    } catch (e) {
      console.error('Take control failed:', e);
    } finally {
      setBusyZone(null);
    }
  };

  const releaseControl = async (zoneNumber) => {
    setBusyZone(zoneNumber);
    try {
      await axios.post(`${API_BASE_URL}/api/event-sessions/${sessionId}/zones/${zoneNumber}/release-control`, {
        user_id: currentUser?.id || 'anonymous'
      });
      if (selectedZone === zoneNumber && onSelectZone) onSelectZone(null);
      fetchStatus();
    } catch (e) {
      console.error('Release failed:', e);
    } finally {
      setBusyZone(null);
    }
  };

  return (
    <Card className="bg-gradient-to-br from-cyan-950/60 to-slate-950/40 border-cyan-700/30" data-testid="zone-control-bar">
      <CardContent className="py-3 px-4">
        <div className="flex items-center justify-between mb-2">
          <p className="text-cyan-200 text-sm font-semibold flex items-center gap-2">
            <Users size={16} /> {(t && t('zoneControl')) || 'Zone Control'}
            <Badge className="bg-cyan-700 text-cyan-100 text-[10px]">{zoneStatus.zone_count} {(t && t('zones')) || 'zones'}</Badge>
          </p>
          {selectedZone && (
            <Badge className={`text-xs ${selectedZone === 1 ? 'bg-blue-600' : 'bg-emerald-600'} text-white`}>
              <AlertTriangle size={10} className="mr-1" />
              {(t && t('viewingZone')) || 'Viewing'} Z{selectedZone}
            </Badge>
          )}
        </div>
        <div className={`grid ${zoneStatus.zone_count === 2 ? 'grid-cols-2' : 'grid-cols-1'} gap-3`}>
          {zoneStatus.zones.map(z => {
            const ctrl = z.controller;
            const isMine = ctrl && ctrl.user_id === (currentUser?.id || 'anonymous');
            const isAssigned = (zoneConfig.zones || []).find(zc => zc.zone_number === z.zone_number)?.assigned_user_ids?.includes(currentUser?.id);
            const accent = z.zone_number === 1 ? 'border-blue-500/50 bg-blue-950/30' : 'border-emerald-500/50 bg-emerald-950/30';
            return (
              <div key={z.zone_number} className={`rounded-lg border-2 p-3 ${accent} ${selectedZone === z.zone_number ? 'ring-2 ring-cyan-400' : ''}`} data-testid={`zone-card-${z.zone_number}`}>
                <div className="flex items-center justify-between mb-2">
                  <span className={`text-base font-bold ${z.zone_number === 1 ? 'text-blue-200' : 'text-emerald-200'}`}>{(t && t('zone')) || 'Zone'} {z.zone_number}</span>
                  {isAssigned && <Badge className="bg-yellow-600/80 text-yellow-50 text-[9px]">{(t && t('preferredZone')) || 'PREFERRED'}</Badge>}
                </div>
                {ctrl ? (
                  <div className="flex items-center gap-2 mb-2 bg-slate-800/60 rounded px-2 py-1.5">
                    <Lock size={12} className={isMine ? 'text-emerald-400' : 'text-amber-400'} />
                    <span className="text-xs text-slate-200 flex-1 truncate">{ctrl.user_name}</span>
                    {isMine && <Badge className="bg-emerald-700 text-emerald-100 text-[9px]">{(t && t('you')) || 'YOU'}</Badge>}
                  </div>
                ) : (
                  <div className="flex items-center gap-2 mb-2 bg-slate-800/30 rounded px-2 py-1.5">
                    <Unlock size={12} className="text-slate-500" />
                    <span className="text-xs text-slate-400 italic">{(t && t('zoneFree')) || 'Free — anyone can take control'}</span>
                  </div>
                )}
                <div className="flex gap-2">
                  {isMine ? (
                    <Button size="sm" className="bg-emerald-700 hover:bg-emerald-800 text-white flex-1" onClick={() => releaseControl(z.zone_number)} disabled={busyZone === z.zone_number} data-testid={`release-zone-${z.zone_number}`}>
                      <Unlock size={12} className="mr-1" /> {(t && t('releaseControl')) || 'Release'}
                    </Button>
                  ) : (
                    <Button size="sm" className={`flex-1 ${z.zone_number === 1 ? 'bg-blue-600 hover:bg-blue-700' : 'bg-emerald-600 hover:bg-emerald-700'} text-white`} onClick={() => takeControl(z.zone_number)} disabled={busyZone === z.zone_number} data-testid={`take-zone-${z.zone_number}`}>
                      <Lock size={12} className="mr-1" /> {(t && t('takeControl')) || 'Take Control'}
                    </Button>
                  )}
                  {isMine && (
                    <Button size="sm" variant="outline" className="border-cyan-500 text-cyan-300" onClick={() => onSelectZone && onSelectZone(selectedZone === z.zone_number ? null : z.zone_number)}>
                      {selectedZone === z.zone_number ? (t && t('showAll')) || 'Show All' : (t && t('view')) || 'View'}
                    </Button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </CardContent>
    </Card>
  );
};

export default ZoneControlBar;
